import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

const PRICING_KEYS = [
  { key: 'astrologer_plan_monthly_price',    label: 'Preço Mensal (R$)',        type: 'number', hint: 'Valor cobrado por mês no plano mensal' },
  { key: 'astrologer_plan_annual_price',     label: 'Preço Anual (R$)',         type: 'number', hint: 'Valor total cobrado no plano anual' },
  { key: 'astrologer_plan_annual_discount_pct', label: 'Desconto Anual (%)',    type: 'number', hint: 'Percentual de desconto exibido para o plano anual' },
  { key: 'astrologer_trial_days',            label: 'Período de Trial (dias)',   type: 'number', hint: 'Dias de acesso gratuito para novos astrólogos' },
];

const DISCOUNT_KEYS = [
  { key: 'astrologer_discount_enabled', label: 'Desconto Ativo',        type: 'boolean', hint: 'Ativa ou desativa o código de desconto' },
  { key: 'astrologer_discount_code',    label: 'Código de Desconto',     type: 'text',    hint: 'Código que o astrólogo digita para obter desconto' },
  { key: 'astrologer_discount_pct',     label: 'Percentual de Desconto (%)', type: 'number', hint: 'Percentual de desconto aplicado ao usar o código' },
];

export function AdminAstrologerPricingPage() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const allKeys = [...PRICING_KEYS, ...DISCOUNT_KEYS].map(k => k.key);

  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key, value')
      .in('key', allKeys)
      .then(({ data }) => {
        const m: Record<string, string> = {};
        for (const s of (data ?? [])) m[s.key] = s.value === null || s.value === undefined ? '' : String(s.value);
        setSettings(m);
        setFetching(false);
      });
  }, []);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    const upserts = Object.entries(settings).map(([key, value]) => ({
      key,
      value,
      updated_at: new Date().toISOString(),
    }));
    await supabase.from('platform_settings').upsert(upserts, { onConflict: 'key' });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '10px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'var(--bg-input)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '8px',
    padding: '0.625rem 0.75rem',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-body)',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-caption)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: '0.3rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '0.3rem',
  };

  if (fetching) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando configurações…</div>;

  // Derived display: monthly / annual comparison
  const monthly = parseFloat(settings['astrologer_plan_monthly_price'] ?? '0');
  const annual  = parseFloat(settings['astrologer_plan_annual_price'] ?? '0');
  const annualMonthly = annual > 0 ? (annual / 12).toFixed(2) : '—';
  const savings = monthly > 0 && annual > 0 ? ((monthly * 12 - annual) / (monthly * 12) * 100).toFixed(0) : '—';

  return (
    <div style={{ maxWidth: '680px' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-h3)', fontWeight: 700 }}>Planos & Preços — Portal do Astrólogo</h2>
        <p style={{ margin: '0.35rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          Configure os valores cobrados pelo módulo profissional e os descontos disponíveis.
        </p>
      </div>

      {/* Pricing summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Mensal', value: monthly > 0 ? `R$ ${monthly.toFixed(2)}` : '—', sub: 'por mês' },
          { label: 'Anual', value: annual > 0 ? `R$ ${annual.toFixed(2)}` : '—', sub: `≈ R$ ${annualMonthly}/mês` },
          { label: 'Economia', value: savings !== '—' ? `${savings}%` : '—', sub: 'no plano anual' },
        ].map(({ label, value, sub }) => (
          <div key={label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
            <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</p>
            <p style={{ margin: '0 0 0.15rem', fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</p>
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{sub}</p>
          </div>
        ))}
      </div>

      {/* Pricing config */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: 'var(--text-body)', fontWeight: 700 }}>Configuração de Preços</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {PRICING_KEYS.map(({ key, label, type, hint }) => (
            <div key={key}>
              <label style={labelStyle}>{label}</label>
              <input
                type={type === 'number' ? 'number' : 'text'}
                min={0}
                step={type === 'number' ? '0.01' : undefined}
                value={settings[key] ?? ''}
                onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
                style={inputStyle}
              />
              <p style={hintStyle}>{hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Discount config */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: 'var(--text-body)', fontWeight: 700 }}>Código de Desconto</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {DISCOUNT_KEYS.map(({ key, label, type, hint }) => {
            if (type === 'boolean') {
              return (
                <div key={key} style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={settings[key] === 'true'}
                      onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.checked ? 'true' : 'false' }))}
                      style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 600, fontSize: 'var(--text-body)' }}>{label}</span>
                  </label>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{hint}</span>
                </div>
              );
            }
            return (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input
                  type={type === 'number' ? 'number' : 'text'}
                  min={0}
                  value={settings[key] ?? ''}
                  onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
                  style={inputStyle}
                  disabled={settings['astrologer_discount_enabled'] !== 'true'}
                />
                <p style={hintStyle}>{hint}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'flex-end' }}>
        {saved && <span style={{ fontSize: 'var(--text-caption)', color: 'var(--success)' }}>✓ Salvo com sucesso</span>}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: saving ? 'var(--border-subtle)' : 'var(--petrol)',
            color: '#fff', border: 'none', borderRadius: '8px',
            padding: '0.625rem 1.5rem', fontSize: 'var(--text-body)',
            fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? 'Salvando…' : 'Salvar Configurações'}
        </button>
      </div>
    </div>
  );
}
